import type {
  AppRoutes,
  CreateGroupEventRoute,
  CreateStorylineEventRoute,
  EditEventCoorganizerRoute,
  EditEventRoute,
  EditTownhallEventCoorganizerRoute,
  EditTownhallEventRoute,
  EventDismissedConversationsSubPath,
  EventInReviewConversationsSubPath,
  EventPublishedConversationsSubPath,
  EventRoute,
  InsightsEventRoute,
  NovaInsightsEventRoute,
  RedirectEventRoute,
  TownhallEventRoute,
  UserEventsRoute,
} from '@ms/yammer-page-route-telemetry';
import { personalizedInboxMatchPath } from '@ms/yammer-personalized-inbox';
import { searchLegacyPath, searchPath } from '@ms/yammer-search-components/dist/urls';
import {
  userSettingsGeneralSubpath,
  userSettingsNotificationsCommunitiesSubpath,
  userSettingsNotificationsSubpath,
  userSettingsRootPath,
} from '@ms/yammer-user-settings-page';
import {
  adminSettingsGroupAgentEnabledCommunitiesUrl,
  articleEditPageMatchPath,
  articleNewMatchPath,
  articlePageMatchPath,
  commsActivityFeaturedConversationsSubPageUrl,
  commsActivitySubPageUrl,
  communicationsDashboardPageRootUrl,
  conversationsSugPageUrl,
  digitalSafetySubPageUrl,
  engageAgentRootPath,
  feedPageUrl,
  groupAboutSubpath,
  groupAgentSettingsSubpath,
  groupAgentSuggestionsSubpath,
  groupCampaignsSubpath,
  groupEventsLiveSubpath,
  groupEventsPastSubpath,
  groupEventsSubpath,
  groupEventsUpcomingSubpath,
  groupFeedAllOrGenericFeedSubpath,
  groupFeedNewSubpath,
  groupFeedNoRepliesQuestionsSubpath,
  groupFeedQuestionsSubpath,
  groupFeedUnansweredQuestionsSubpath,
  groupFilesSubpath,
  groupMembersSubPath,
  groupParentPath,
  groupPinnedLinksSubpath,
  groupRelatedCommunitiesSubpath,
  groupSettingsSubpath,
  inboxAllSubpath,
  inboxDirectMessagesSubpath,
  inboxRootPath,
  inboxUnreadSubpath,
  insightsConversationInsightsSubpath,
  insightsGroupSeededPage,
  insightsMyInsightsSubpath,
  insightsNetworkSubpath,
  insightsPremiumGroupOptimizationsPage,
  insightsPremiumSelectedGroupSubpath,
  insightsQnAMyInsightsSubpath,
  insightsQnAOrgSubpath,
  insightsRootPath,
  insightsSelectedAudienceLeaderSubpath,
  insightsSelectedCampaignSubpath,
  insightsSelectedLeaderMyInsightsSubpath,
  leadersActivityLogSugPageUrl,
  leadersAndAudiencesSubPageUrl,
  legacyInsightsConversationInsightsPage,
  m365CopilotAdoptionPageUrl,
  networkThemesFeedMatchPath,
  questionRoutePath,
  takeActionNotificationMatchPath,
  takeActionRootPath,
  threadRoutePath,
  topicFeedAllSubpath,
  topicFeedConversationsSubpath,
  topicFeedConversationsUnseenSubpath,
  topicFeedQuestionsSubpath,
  topicFeedQuestionsUnresolvedSubpath,
  topicFeedQuestionsUnseenSubpath,
  topicParentPath,
  userPendingPostArticlePath,
  userPendingPostThreadPath,
  userPendingPostsPath,
} from '@ms/yammer-web-support/dist/urls';

export const pageRoutes: AppRoutes = [
  {
    page: 'ArticleNew',
    path: articleNewMatchPath,
    exact: true,
    hero: ['article_publisher'],
    loader: () => import(/* webpackChunkName: "4-page-article-publisher" */ 'pages/PageArticleNew'),
  },
  {
    page: 'ArticleEdit',
    path: articleEditPageMatchPath,
    exact: true,
    hero: ['article_publisher'],
    loader: () => import(/* webpackChunkName: "4-page-article-publisher" */ 'pages/PageArticleEdit'),
  },
  {
    page: 'Article',
    path: articlePageMatchPath,
    exact: true,
    hero: ['article', 'thread_deleted_or_private'],
    loader: () => import(/* webpackChunkName: "4-page-article" */ 'pages/PageArticle'),
  },
  {
    path: communicationsDashboardPageRootUrl,
    // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    loader: () => import(/* webpackChunkName: "4-page-dashboard-root" */ '../../pages/PageCommsDashboardRoot'),
    childRoutes: [
      {
        page: 'CommunicationsDashboard',
        path: commsActivitySubPageUrl,
        exact: true,
        // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
        // oxlint-disable-next-line @ms/no-bad-route-config
        hero: ['none'],
        loader: () =>
          import(
            /* webpackChunkName: "4-page-dashboard-featured-conversations" */ '../../pages/PageDashboardCommunications'
          ),
      },
      {
        page: 'CommsDashboardFeaturedConversations',
        path: commsActivityFeaturedConversationsSubPageUrl,
        exact: true,
        // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
        // oxlint-disable-next-line @ms/no-bad-route-config
        hero: ['none'],
        loader: () =>
          import(
            /* webpackChunkName: "4-page-dashboard-featured-conversations" */ '../../pages/PageDashboardFeaturedConversations'
          ),
      },
      {
        page: 'CommsDashboardConversations',
        path: conversationsSugPageUrl,
        exact: true,
        // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
        // oxlint-disable-next-line @ms/no-bad-route-config
        hero: ['none'],
        loader: () =>
          import(/* webpackChunkName: "4-page-dashboard-conversations" */ '../../pages/PageDashboardConversations'),
      },
      {
        page: 'CommunicationsDashboardAdvancedModeration',
        path: digitalSafetySubPageUrl,
        exact: true,
        hero: ['communications_dashboard_advanced_moderation', 'communications_dashboard_legacy_advanced_moderation'],
        loader: () =>
          import(
            /* webpackChunkName: "4-page-dashboard-advanced-moderation" */ '../../pages/PageDashboardDigitalSafety'
          ),
      },
      {
        page: 'CommunicationsDashboardLeadersAndAudiences',
        path: leadersAndAudiencesSubPageUrl,
        exact: true,
        // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
        // oxlint-disable-next-line @ms/no-bad-route-config
        hero: ['none'],
        loader: () =>
          import(
            /* webpackChunkName: "4-page-dashboard-leader-and-audiences" */ '../../pages/PageDashboardLeadersAndAudiences'
          ),
      },
      {
        page: 'CommunicationsDashboardLeadersActivityLog',
        path: leadersActivityLogSugPageUrl,
        exact: true,
        // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
        // oxlint-disable-next-line @ms/no-bad-route-config
        hero: ['none'],
        loader: () =>
          import(/* webpackChunkName: "4-page-dashboard-leader-log" */ '../../pages/PageDashboardLeadersActivityLog'),
      },
    ],
    fallbackRedirectPath: feedPageUrl,
  },
  {
    page: 'MediaPostImmersiveView',
    path: '/media-post/:threadId',
    exact: true,
    hero: ['thread'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-media-post-immersive-view" */ '@ms/yammer-web-components/dist/mediaPost/PageMediaPostImmersiveView'
      ),
  },
  {
    page: 'AMAGroupCreate',
    path: '/ama/new/group/:groupId' satisfies CreateGroupEventRoute,
    exact: true,
    hero: ['ama_event_create', 'create_premium_event_form', 'content_inaccessible', 'ama_not_authorized'],
    loader: () => import(/* webpackChunkName: "4-page-ama-event-creation" */ 'pages/PageAskMeAnythingEventCreation'),
  },
  {
    page: 'AMACreate',
    path: '/ama/new/:userId?' satisfies CreateStorylineEventRoute,
    exact: true,
    hero: ['ama_event_create', 'create_premium_event_form', 'content_inaccessible', 'ama_not_authorized'],
    loader: () => import(/* webpackChunkName: "4-page-ama-new" */ 'pages/PageAskMeAnythingEventCreation'),
  },
  {
    page: 'AMAEventRedirect',
    path: '/ama/redirect/:teamsMeetingId' satisfies RedirectEventRoute,
    exact: true,
    hero: ['ama_event_redirect', 'content_inaccessible'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-ama-redirect" */ '@ms/yammer-premium-event/dist/pages/RedirectPremiumEventPage'
      ),
  },
  {
    page: 'AMAEdit',
    path: '/ama/:teamsMeetingId/edit' satisfies EditEventRoute,
    exact: true,
    hero: ['ama_event_edit', 'edit_premium_event_form', 'content_inaccessible'],
    loader: () => import(/* webpackChunkName: "4-page-event-edit" */ 'pages/PageAskMeAnythingEventEditing'),
  },
  {
    page: 'AMACoorganizerEdit',
    path: '/ama/:teamsMeetingId/edit/coorganizer' satisfies EditEventCoorganizerRoute,
    exact: true,
    hero: ['edit_premium_event_coorganizer_form', 'content_inaccessible'],
    loader: () =>
      import(/* webpackChunkName: "4-page-event-coorganizer-edit" */ 'pages/PageAskMeAnythingEventCoorganizerEditing'),
  },
  {
    page: 'AMAInsights',
    path: '/ama/:teamsMeetingId/insights' satisfies InsightsEventRoute,
    exact: true,
    hero: ['ama_event_insights'],
    dataLoader: () =>
      import(
        /* webpackChunkName: "4-pagedata-event-insights" */ '@ms/yammer-features-insights/dist/components/EventActivityDashboard/getPageDataLoader'
      ),
    loader: () =>
      import(
        /* webpackChunkName: "4-page-event-insights" */ '@ms/yammer-features-insights/dist/pages/PageEventActivityDashboard'
      ).then((module) => ({ default: module.PageEventActivityDashboard })),
  },
  {
    page: 'AMANovaInsights',
    path: '/ama/:teamsMeetingId/nova/insights' satisfies NovaInsightsEventRoute,
    exact: true,
    hero: ['nova_event_insights'],
    dataLoader: () =>
      import(/* webpackChunkName: "4-page-nova-event-insights" */ '@ms/yammer-insights-nova-integration').then(
        (module) => ({
          loadData: module.getNovaEventActivityDashboardPageDataLoader,
        })
      ),
    loader: () =>
      import(/* webpackChunkName: "4-page-nova-event-insights" */ '@ms/yammer-insights-page').then((module) => ({
        default: module.PageNovaEventActivityDashboard,
      })),
  },
  {
    path: '/ama/:teamsMeetingId' satisfies EventRoute,
    loader: () =>
      import(
        /* webpackChunkName: "4-page-ama-event" */ '@ms/yammer-web-components/dist/askMeAnything/PageAskMeAnything'
      ),
    childRoutes: [
      {
        page: 'AMAEventInReview',
        path: '/in-review' satisfies EventInReviewConversationsSubPath,
        exact: true,
        hero: ['feed', 'ama_event_redirect', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-ama-in-review" */ '@ms/yammer-web-components/dist/askMeAnything/SubpageAskMeAnythingInReviewConversations'
          ),
      },
      {
        page: 'AMAEventDismissed',
        path: '/dismissed' satisfies EventDismissedConversationsSubPath,
        exact: true,
        hero: ['feed', 'ama_event_redirect', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-ama-dismissed" */ '@ms/yammer-web-components/dist/askMeAnything/SubpageAskMeAnythingDismissedConversations'
          ),
      },
      {
        page: 'AMAEventPublished',
        path: '/published' satisfies EventPublishedConversationsSubPath,
        exact: true,
        hero: ['feed', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-ama-published" */ '@ms/yammer-web-components/dist/askMeAnything/SubpageAskMeAnythingAll'
          ),
      },
    ],
    fallbackRedirectRoute: {
      page: 'AMAFallbackRedirect',
      hero: ['ama_event_redirect', 'content_inaccessible'],
      loader: () =>
        import(
          /* webpackChunkName: "4-subpage-ama-fallback" */ '@ms/yammer-premium-event/dist/pages/PremiumEventPageFallbackRoute'
        ),
    },
  },
  {
    page: 'TownhallEdit',
    path: '/broadcasts/:teamsMeetingId/edit' satisfies EditTownhallEventRoute,
    exact: true,
    hero: ['ama_event_edit', 'edit_premium_event_form', 'content_inaccessible'],
    loader: () => import(/* webpackChunkName: "4-page-event-edit" */ 'pages/PageAskMeAnythingEventEditing'),
  },
  {
    page: 'TownhallCoorganizerEdit',
    path: '/broadcasts/:teamsMeetingId/edit/coorganizer' satisfies EditTownhallEventCoorganizerRoute,
    exact: true,
    hero: ['edit_premium_event_coorganizer_form', 'content_inaccessible'],
    loader: () =>
      import(/* webpackChunkName: "4-page-event-coorganizer-edit" */ 'pages/PageAskMeAnythingEventCoorganizerEditing'),
  },
  {
    path: '/broadcasts/:teamsMeetingId' satisfies TownhallEventRoute,
    loader: () =>
      import(
        /* webpackChunkName: "4-page-townhall-event" */ '@ms/yammer-web-components/dist/askMeAnything/PageTownhallEvent'
      ),
    childRoutes: [
      {
        page: 'TownhallEventInReview',
        path: '/in-review' satisfies EventInReviewConversationsSubPath,
        exact: true,
        hero: ['feed', 'ama_event_redirect', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-event-in-review" */ '@ms/yammer-web-components/dist/askMeAnything/SubpageAskMeAnythingInReviewConversations'
          ),
      },
      {
        page: 'TownhallEventDismissed',
        path: '/dismissed' satisfies EventDismissedConversationsSubPath,
        exact: true,
        hero: ['feed', 'ama_event_redirect', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-event-dismissed" */ '@ms/yammer-web-components/dist/askMeAnything/SubpageAskMeAnythingDismissedConversations'
          ),
      },
      {
        page: 'TownhallEventPublished',
        path: '/published' satisfies EventPublishedConversationsSubPath,
        exact: true,
        hero: ['feed', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-event-published" */ '@ms/yammer-web-components/dist/askMeAnything/SubpageAskMeAnythingAll'
          ),
      },
    ],
    fallbackRedirectRoute: {
      page: 'TownhallEventFallbackRedirect',
      hero: ['ama_event_redirect', 'content_inaccessible'],
      loader: () =>
        import(
          /* webpackChunkName: "4-subpage-event-fallback" */ '@ms/yammer-premium-event/dist/pages/PremiumEventPageFallbackRoute'
        ),
    },
  },
  {
    page: 'AMAUserEvents',
    path: '/users/:userId/events/askmeanything' satisfies UserEventsRoute,
    exact: true,
    hero: ['ama_user_events', 'ama_user_events_empty', 'user_premium_events'],
    loader: () => import(/* webpackChunkName: "4-page-ama-user-events" */ 'pages/PageAskMeAnythingUserEvents'),
  },
  {
    page: 'LiveEventCreate',
    path: '/events/new/:groupId',
    exact: true,
    hero: ['townhall_event_create', 'content_inaccessible'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-liveevent-create-event" */ '@ms/yammer-modules-townhall-event/dist/pages/CreateTownhallEventPage'
      ),
  },
  {
    page: 'LiveEvent',
    path: '/events/:broadcastId/(questions|unanswered)?',
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-liveevent" */ 'pages/PageLiveEvent'),
  },
  {
    page: 'LiveEventEdit',
    path: '/events/:broadcastId/edit',
    exact: true,
    hero: ['townhall_event_edit', 'content_inaccessible'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-liveevent-edit-event" */ '@ms/yammer-modules-townhall-event/dist/pages/EditTownhallEventPage'
      ),
  },
  {
    page: 'HomeFeed',
    path: '/feed',
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-feed" */ 'pages/PageFeed'),
  },
  {
    page: 'EngageAgent',
    path: engageAgentRootPath,
    exact: true,
    hero: ['engage_agent'],
    loader: () => import(/* webpackChunkName: "4-page-engage-agent" */ 'pages/PageEngageAgent'),
  },
  {
    page: 'TakeAction',
    path: takeActionRootPath,
    exact: true,
    hero: ['take_action'],
    loader: () => import(/* webpackChunkName: "4-page-take-action" */ 'pages/PageTakeAction'),
    dataLoader: () => import(/* webpackChunkName: "4-page-take-action" */ '@ms/yammer-take-action-agent'),
  },
  {
    page: 'TakeActionNotification',
    path: takeActionNotificationMatchPath,
    exact: true,
    hero: ['take_action'],
    loader: () => import(/* webpackChunkName: "4-page-take-action" */ 'pages/PageTakeAction'),
    dataLoader: () => import(/* webpackChunkName: "4-page-take-action" */ '@ms/yammer-take-action-agent'),
  },
  {
    page: 'UserAchievements',
    path: '/achievements/:userId',
    exact: true,
    // CorpComms: Report hero as successful/error/recoverable error in all scenarios https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    hero: ['network_question_achievements'],
    loader: () => import(/* webpackChunkName: "4-page-user-achievements" */ 'pages/PageUserAchievements'),
  },
  {
    page: 'StorylinesHome',
    path: '/storylines',
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-storyline-home" */ 'pages/PageStorylineHome'),
  },
  {
    page: 'NetworkThemeFeed',
    path: networkThemesFeedMatchPath,
    exact: true,
    hero: ['feed'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-network-theme" */ '@ms/yammer-web-components/dist/feed/NetworkThemeFeed/PageNetworkThemeFeed'
      ),
  },
  {
    page: 'PersonalizedInbox',
    path: personalizedInboxMatchPath,
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-personalized-inbox" */ 'pages/PageInbox/PagePersonalizedInbox'),
  },
  {
    path: inboxRootPath,
    loader: () => import(/* webpackChunkName: "4-page-inbox" */ 'pages/PageInbox'),
    childRoutes: [
      {
        page: 'InboxUnread',
        path: inboxUnreadSubpath,
        exact: true,
        hero: ['relay_inbox_feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-inbox-unread" */ '@ms/yammer-web-components/dist/inbox/SubpageInboxUnread/SubpageRelayInboxUnread'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-inbox" */ '@ms/yammer-inbox/dist/components/InboxFeed/getPageDataLoader'
          ),
      },
      {
        page: 'InboxDMs',
        path: inboxDirectMessagesSubpath,
        exact: true,
        hero: ['relay_inbox_feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-inbox-dms" */ '@ms/yammer-web-components/dist/inbox/SubpageInboxDirect/SubpageRelayInboxDirect'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-inbox" */ '@ms/yammer-inbox/dist/components/InboxFeed/getPageDataLoader'
          ),
      },
      {
        page: 'InboxAll',
        path: inboxAllSubpath,
        exact: true,
        hero: ['relay_inbox_feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-inbox-all" */ '@ms/yammer-web-components/dist/inbox/SubpageInboxAll/SubpageRelayInboxAll'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-inbox" */ '@ms/yammer-inbox/dist/components/InboxFeed/getPageDataLoader'
          ),
      },
    ],
    fallbackRedirectRoute: {
      page: 'InboxFallback',
      hero: ['inbox_redirect'],
      loader: () =>
        import(
          /* webpackChunkName: "4-subpage-inbox-redirect" */ '@ms/yammer-web-components/dist/inbox/SubpageInboxFallback'
        ),
    },
  },
  {
    page: 'CampaignsDashboard',
    path: '/admin/campaigns-dashboard',
    exact: true,
    // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () => import(/* webpackChunkName: "4-page-campaigns-dashboard" */ 'pages/PageCampaignsDashboard'),
  },
  {
    // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    // oxlint-disable-next-line @ms/no-bad-route-config
    page: 'CampaignsDashboardWithSubroute',
    // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    // oxlint-disable-next-line @ms/no-bad-route-config
    path: '/admin/campaigns-dashboard/:campaignsDashboardSubRoute',
    exact: true,
    // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () => import(/* webpackChunkName: "4-page-campaigns-dashboard" */ 'pages/PageCampaignsDashboard'),
  },
  {
    page: 'CampaignsHome',
    path: '/campaigns-hub',
    exact: true,
    // CorpComms: https://yammer.visualstudio.com/engineering/_workitems/edit/344118
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () => import(/* webpackChunkName: "4-page-campaigns-hub" */ 'pages/PageCampaignsHub'),
  },
  {
    page: 'CampaignInsights',
    path: '/campaigns/:campaignId/insights',
    exact: true,
    hero: ['campaign_people_reached_activity'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-campaign-insights" */ '@ms/yammer-web-components/dist/insights/PageCampaignInsights'
      ),
  },
  {
    page: 'Campaign',
    path: '/campaigns/:campaignId',
    exact: true,
    hero: ['feed', 'campaign_deleted', 'campaign_failed', 'campaign_inaccessible', 'group_campaign_inaccessible'],
    loader: () => import(/* webpackChunkName: "4-page-campaign" */ 'pages/PageCampaign'),
  },
  /**
   * All of these non-nested group routes do not align to a consistent Information Architecture and cannot be nested as-is.
   */
  {
    page: 'GroupsDiscover',
    // Communities: Migrate to nested routes: https://yammer.visualstudio.com/engineering/_workitems/edit/344125
    path: '/discover-groups',
    exact: true,
    hero: ['all_groups_list', 'recommended_groups_list', 'official_groups_list', 'my_groups'],
    loader: () => import(/* webpackChunkName: "4-page-discover-groups" */ '../../pages/PageDiscoverGroups'),
  },
  {
    // Communities: Migrate to nested routes: https://yammer.visualstudio.com/engineering/_workitems/edit/344125
    // oxlint-disable-next-line @ms/no-bad-route-config
    page: 'GroupsDiscoverWithSubroute',
    // Communities: https://yammer.visualstudio.com/engineering/_workitems/edit/344125
    // oxlint-disable-next-line @ms/no-bad-route-config
    path: '/discover-groups/:discoverGroupsSubRoute',
    exact: true,
    hero: ['all_groups_list', 'recommended_groups_list', 'official_groups_list', 'my_groups'],
    loader: () => import(/* webpackChunkName: "4-page-discover-groups" */ '../../pages/PageDiscoverGroups'),
  },
  {
    page: 'M365CopilotSuggestedContent',
    path: '/groups/:groupId/m365-copilot-suggested-content',
    exact: true,
    // AIR: https://yammer.visualstudio.com/engineering/_workitems/edit/344123
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-m365-copilot-suggested-content" */ '../../pages/PageM365CopilotSuggestedContent'
      ),
  },
  {
    page: 'M365CopilotSuggestedArticle',
    path: '/groups/:groupId/suggested-article',
    exact: true,
    // AIR: https://yammer.visualstudio.com/engineering/_workitems/edit/344123
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () =>
      import(/* webpackChunkName: "4-page-suggested-article" */ '../../pages/PageM365CopilotSuggestedArticle'),
  },
  {
    page: 'GroupTopQuestions',
    path: '/groups/:groupId/top-questions',
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-top-questions" */ 'pages/PageGroupTopQuestions'),
  },
  {
    page: 'GroupCuratedThreads',
    path: '/groups/:groupId/curated-thread-list/:curatedThreadListId/:sourceFileId?',
    exact: true,
    hero: ['curated_thread_list'],
    loader: () => import(/* webpackChunkName: "4-page-curated-threads" */ 'pages/PageCuratedThreadList'),
  },
  {
    path: groupParentPath,
    loader: () => import(/* webpackChunkName: "4-page-group" */ 'pages/PageGroup'),
    childRoutes: [
      {
        page: 'GroupUnseen',
        path: groupFeedNewSubpath,
        exact: true,
        hero: ['feed', 'group_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-feed" */ '@ms/yammer-web-components/dist/group/SubpageGroupFeedUnseen'
          ),
      },
      {
        page: 'GroupAll',
        path: groupFeedAllOrGenericFeedSubpath,
        exact: true,
        hero: ['feed', 'group_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-feed" */ '@ms/yammer-web-components/dist/group/SubpageGroupFeedAll'
          ),
      },
      {
        page: 'GroupQuestions',
        path: groupFeedQuestionsSubpath,
        exact: true,
        hero: ['feed', 'group_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-feed" */ '@ms/yammer-web-components/dist/group/SubpageGroupFeedQuestions'
          ),
      },
      {
        page: 'GroupUnansweredQuestions',
        path: groupFeedUnansweredQuestionsSubpath,
        exact: true,
        hero: ['feed', 'group_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-feed" */ '@ms/yammer-web-components/dist/group/SubpageGroupFeedUnansweredQuestions'
          ),
      },
      {
        page: 'GroupNoRepliesQuestions',
        path: groupFeedNoRepliesQuestionsSubpath,
        exact: true,
        hero: ['feed', 'group_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-feed" */ '@ms/yammer-web-components/dist/group/SubpageGroupFeedNoRepliesQuestions'
          ),
      },
      {
        page: 'GroupAbout',
        path: groupAboutSubpath,
        exact: true,
        hero: ['group_about', 'group_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-about" */ '@ms/yammer-web-components/dist/group/SubpageGroupAbout'
          ),
      },
      {
        page: 'GroupRelatedCommunities',
        path: groupRelatedCommunitiesSubpath,
        exact: true,
        hero: ['group_related_communities'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-related-communities" */ '@ms/yammer-web-components/dist/group/SubpageGroupRelatedCommunities'
          ),
      },
      {
        page: 'GroupEvents',
        path: groupEventsSubpath,
        exact: true,
        hero: ['group_events', 'group_inaccessible', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-events" */ '@ms/yammer-web-components/dist/group/SubpageGroupEvents'
          ),
      },
      {
        page: 'GroupEventsUpcoming',
        path: groupEventsUpcomingSubpath,
        exact: true,
        hero: ['group_events_list', 'group_inaccessible', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-events-upcoming" */ '@ms/yammer-web-components/dist/group/SubpageGroupEvents/SubpageGroupEventsUpcoming'
          ),
      },
      {
        page: 'GroupEventsPast',
        path: groupEventsPastSubpath,
        exact: true,
        hero: ['group_events_list', 'group_inaccessible', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-events-past" */ '@ms/yammer-web-components/dist/group/SubpageGroupEvents/SubpageGroupEventsPast'
          ),
      },
      {
        page: 'GroupEventsLive',
        path: groupEventsLiveSubpath,
        exact: true,
        hero: ['group_events_list', 'group_inaccessible', 'content_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-events-live" */ '@ms/yammer-web-components/dist/group/SubpageGroupEvents/SubpageGroupEventsLive'
          ),
      },
      {
        page: 'GroupCampaigns',
        path: groupCampaignsSubpath,
        exact: true,
        hero: ['group_campaigns', 'group_campaigns_empty', 'group_campaign_inaccessible'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-campaigns" */ '@ms/yammer-web-components/dist/groupCampaignsMain/SubpageGroupCampaignsBoundary'
          ),
      },
      {
        page: 'GroupPinnedLinks',
        path: groupPinnedLinksSubpath,
        exact: true,
        hero: ['group_pinned_links'],
        loader: () => import(/* webpackChunkName: "4-page-group-pinned-links" */ 'pages/PageGroupPinnedLinks'),
      },
      {
        page: 'GroupFiles',
        path: groupFilesSubpath,
        exact: true,
        hero: ['group_files', 'group_inaccessible'],
        loader: () => import(/* webpackChunkName: "4-subpage-group-files" */ 'pages/SubpageGroupFiles'),
      },
      {
        page: 'GroupSettings',
        path: groupSettingsSubpath,
        exact: true,
        hero: ['group_settings_form', 'group_inaccessible'],
        loader: () => import(/* webpackChunkName: "4-subpage-group-settings" */ 'pages/PageGroupSettings'),
      },
      {
        page: 'GroupMembers',
        path: groupMembersSubPath,
        exact: true,
        hero: ['group_members_list', 'group_inaccessible', 'group_deleted'],
        loader: () => import(/* webpackChunkName: "4-subpage-group-members" */ 'pages/PageGroupMembers'),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-group-members" */ '@ms/yammer-groups/dist/components/GroupMembers/GroupMembersDataLoader'
          ),
      },
      {
        page: 'GroupAgentSuggestions',
        path: groupAgentSuggestionsSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(/* webpackChunkName: "4-subpage-group-agent-suggestions" */ '@ms/yammer-group-agent-page/suggestions'),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-group-agent-suggestions" */ '@ms/yammer-group-agent-page/suggestions-data'
          ),
      },
      {
        page: 'GroupAgentSettings',
        path: groupAgentSettingsSubpath,
        exact: true,
        hero: ['group_agent_settings'],
        loader: () =>
          import(/* webpackChunkName: "4-subpage-group-agent-settings" */ '@ms/yammer-group-agent-page/settings'),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-subpage-group-agent-settings-data" */ '@ms/yammer-group-agent-page/settings-data'
          ),
      },
    ],
    fallbackRedirectRoute: {
      page: 'GroupFeedFallbackRedirect',
      hero: ['group_feed_fallback_redirect'],
      loader: () =>
        import(
          /* webpackChunkName: "4-subpage-group-fallbackredirect" */ '@ms/yammer-web-components/dist/group/SubpageGroupFeedFallback'
        ),
    },
  },
  {
    page: 'AdminCopilotAdoption',
    path: m365CopilotAdoptionPageUrl,
    exact: true,
    // AIR: https://yammer.visualstudio.com/engineering/_workitems/edit/344123
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () => import(/* webpackChunkName: "4-page-admin-m365-copilot-adoption" */ 'pages/PageM365CopilotAdoption'),
  },
  {
    page: 'LegacyThreadInsightsRedirect',
    path: legacyInsightsConversationInsightsPage,
    exact: true,
    // Data Platform: https://yammer.visualstudio.com/engineering/_workitems/edit/344131
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-legacy-thread-insights-redirect" */ '@ms/yammer-web-components/dist/thread/LegacyThreadInsightsRedirect'
      ),
  },
  {
    page: 'Thread',
    path: threadRoutePath,
    exact: true,
    hero: ['thread', 'thread_deleted_or_private'],
    loader: () => import(/* webpackChunkName: "4-page-thread" */ 'pages/PageThread'),
    dataLoader: () =>
      import(/* webpackChunkName: "4-pagedata-thread" */ '@ms/yammer-data/dist/domains/threadLoad/getThreadLoader'),
  },
  {
    page: 'UserPendingPostArticle',
    path: userPendingPostArticlePath,
    exact: true,
    hero: ['article', 'thread_deleted_or_private'],
    loader: () => import(/* webpackChunkName: "4-page-pending-post-article" */ 'pages/PageDraftArticle'),
    dataLoader: () =>
      import(/* webpackChunkName: "4-pagedata-thread" */ '@ms/yammer-data/dist/domains/threadLoad/getThreadLoader'),
  },
  {
    page: 'UserPendingPost',
    path: userPendingPostThreadPath,
    exact: true,
    hero: ['thread', 'thread_deleted_or_private'],
    loader: () => import(/* webpackChunkName: "4-page-pending-post" */ 'pages/PageDraftThread'),
    dataLoader: () =>
      import(/* webpackChunkName: "4-pagedata-thread" */ '@ms/yammer-data/dist/domains/threadLoad/getThreadLoader'),
  },
  {
    page: 'UserPendingPosts',
    path: userPendingPostsPath,
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-pending-posts" */ 'pages/PagePendingPosts'),
  },
  {
    page: 'UserFeed',
    // Storyline: https://yammer.visualstudio.com/engineering/_workitems/edit/344135
    // oxlint-disable-next-line @ms/no-bad-route-config
    path: '/users/:userId/:userFeedSubRoute?',
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-user" */ 'pages/PageUser'),
  },
  {
    path: topicParentPath,
    loader: () => import(/* webpackChunkName: "4-page-topic" */ 'pages/PageTopic'),
    childRoutes: [
      {
        page: 'TopicAll',
        path: topicFeedAllSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-topic-feed" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicAll'
          ),
      },
      {
        page: 'TopicQuestionsAll',
        path: topicFeedQuestionsSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-topic-feed" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicQuestions'
          ),
      },
      {
        page: 'TopicQuestionUnseen',
        path: topicFeedQuestionsUnseenSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-topic-feed" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicQuestionsUnseen'
          ),
      },
      {
        page: 'TopicQuestionUnresolved',
        path: topicFeedQuestionsUnresolvedSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-topic-feed" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicQuestionsUnresolved'
          ),
      },
      {
        page: 'TopicConversationsAll',
        path: topicFeedConversationsSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-topic-feed" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicConversations'
          ),
      },
      {
        page: 'TopicConversationsUnseen',
        path: topicFeedConversationsUnseenSubpath,
        exact: true,
        hero: ['feed'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-topic-feed" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicConversationsUnseen'
          ),
      },
    ],
    fallbackRedirectRoute: {
      page: 'TopicFeedFallbackRedirect',
      hero: ['topic_fallback_redirect'],
      loader: () =>
        import(
          /* webpackChunkName: "4-subpage-topic-fallbackredirect" */ '@ms/yammer-web-components/dist/topic/SubpageUnifiedTopicFallback'
        ),
    },
  },
  {
    page: 'UsagePolicy',
    path: '/usage-policy',
    exact: true,
    hero: ['usage_policy'],
    loader: () => import(/* webpackChunkName: "4-page-usagepolicy" */ 'pages/PageUsagePolicy'),
  },
  {
    page: 'UserDelegationSettings',
    path: '/user-settings/delegates',
    exact: true,
    hero: ['delegation_settings'],
    loader: () => import(/* webpackChunkName: "4-page-delegation-settings" */ 'pages/PageDelegationSettings'),
  },
  {
    page: 'Configure',
    path: '/configure',
    exact: true,
    hero: ['teams_configure_search_results'],
    loader: () => import(/* webpackChunkName: "4-page-configure" */ 'pages/PageConfigure'),
  },
  {
    page: 'Search',
    path: searchPath,
    exact: true,
    hero: ['search_results'],
    loader: () => import(/* webpackChunkName: "4-page-search" */ 'pages/PageSearch'),
  },
  {
    page: 'SearchLegacyRedirect',
    path: searchLegacyPath,
    exact: true,
    // Search: https://yammer.visualstudio.com/engineering/_workitems/edit/344129
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-search-legacy" */ '@ms/yammer-search-serp/dist/components/SearchLegacyRedirect'
      ),
  },
  {
    page: 'QuestionThread',
    path: questionRoutePath,
    exact: true,
    hero: ['question_thread', 'question_thread_deleted_or_private'],
    loader: () => import(/* webpackChunkName: "4-page-question-thread" */ 'pages/PageQuestionThread'),
  },
  {
    page: 'Questions',
    path: '/questions',
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-questions" */ 'pages/PageQuestions'),
  },
  {
    page: 'QuestionsTopicsBrowse',
    path: '/topics-network-question/browse',
    exact: true,
    hero: ['topic_card_list'],
    loader: () =>
      import(/* webpackChunkName: "4-page-topic-networkquestion-browse" */ 'pages/PageTopicNetworkQuestionBrowse'),
  },
  {
    page: 'UserAudienceSettings',
    path: '/user-settings/leaders/manage/:userId',
    exact: true,
    // Leadership: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    // NOTE: This doesn't align with the Web implementation where this route is a legacy redirect
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () => import(/* webpackChunkName: "4-page-manage-audience" */ 'pages/PageManageAudience'),
  },
  {
    path: userSettingsRootPath,
    loader: () =>
      import(/* webpackChunkName: "4-page-user-settings" */ '@ms/yammer-user-settings-page/dist/UserSettingsPage').then(
        (module) => ({ default: module.UserSettingsPage })
      ),
    childRoutes: [
      {
        page: 'UserSettingsGeneralSettings',
        path: userSettingsGeneralSubpath,
        exact: true,
        hero: ['user_skin_tone_settings'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-user-settings-general" */ '../../pages/PageUserSettings/SubpageUserSettingsGeneral'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-user-settings-general-loader" */ '@ms/yammer-user-settings-page/dist/GeneralSettingsMetaOSLoader'
          ),
      },
      {
        page: 'UserSettingsNotificationsSettings',
        path: userSettingsNotificationsSubpath,
        exact: true,
        hero: ['daily_digest_notifications_settings'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-user-settings-notifications-settings" */ '@ms/yammer-user-settings-page/dist/subpages/NotificationsSettings/NotificationsSettings'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-user-settings-loader" */ '@ms/yammer-user-settings-page/dist/UserNotificationSettingsLoader'
          ),
      },
      {
        page: 'UserSettingsNotificationsCommunity',
        path: userSettingsNotificationsCommunitiesSubpath,
        exact: true,
        hero: ['notifications_community'],
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-user-settings-notifications-community" */ '@ms/yammer-user-settings-page/dist/subpages/NotificationsCommunity/NotificationsCommunity'
          ),
      },
    ],
    fallbackRedirectPath: feedPageUrl,
  },
  {
    page: 'Leaders',
    path: '/leaders',
    exact: true,
    // Leadership: Migrate to nested routes: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    hero: ['leaders_list_all', 'leaders_list_connected', 'leaders_list_followed'],
    loader: () => import(/* webpackChunkName: "4-page-leaders-directory" */ 'pages/PageLeadersDirectory'),
  },
  {
    page: 'LeadersConnected',
    path: '/leaders/connected',
    exact: true,
    // Leadership: Migrate to nested routes: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    hero: ['leaders_list_connected'],
    loader: () => import(/* webpackChunkName: "4-page-leaders-directory" */ 'pages/PageLeadersDirectory'),
  },
  {
    page: 'LeadersFollowed',
    path: '/leaders/followed',
    exact: true,
    // Leadership: Migrate to nested routes: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    hero: ['leaders_list_followed'],
    loader: () => import(/* webpackChunkName: "4-page-leaders-directory" */ 'pages/PageLeadersDirectory'),
  },
  {
    page: 'LeadersAll',
    path: '/leaders/all',
    exact: true,
    // Leadership: Migrate to nested routes: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    hero: ['leaders_list_all'],
    loader: () => import(/* webpackChunkName: "4-page-leaders-directory" */ 'pages/PageLeadersDirectory'),
  },
  {
    page: 'LeadershipCorner',
    path: '/leadership-corner',
    exact: true,
    hero: ['feed'],
    loader: () => import(/* webpackChunkName: "4-page-leadershipcorner" */ 'pages/PageLeadershipCorner'),
  },
  {
    page: 'LeaderDashboard',
    path: '/leadership-corner/leader-dashboard',
    exact: true,
    // Leadership: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () => import(/* webpackChunkName: "4-page-leader-dashboard" */ 'pages/PageLeadershipCorner'),
  },
  {
    page: 'LeadersCuratedFeed',
    path: '/leadership-corner/featured-conversations',
    exact: true,
    // Leadership: https://yammer.visualstudio.com/engineering/_workitems/edit/344117
    // oxlint-disable-next-line @ms/no-bad-route-config
    hero: ['none'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-leaders-curated-feed" */ '@ms/yammer-web-components/dist/leadersCuratedFeed/PageLeadersCuratedFeedMain'
      ),
  },
  {
    page: 'GroupInsightsOptimizer',
    path: insightsPremiumGroupOptimizationsPage,
    hero: ['group_optimizer_lost_conversations'],
    exact: true,
    loader: () =>
      import(
        /* webpackChunkName: "4-page-insights-group-optimization" */ '@ms/yammer-web-components/dist/insights/PageInsightsGroupOptimization'
      ),
    dataLoader: () =>
      import(
        /* webpackChunkName: "4-pagedata-community-optimization-access" */ '@ms/yammer-features-insights/dist/pages/PageCommunityOptimization/communityOptimizationAccessDataLoader'
      ),
  },
  {
    page: 'GroupSeededInsights',
    path: insightsGroupSeededPage,
    exact: true,
    hero: ['group_analytics_members_activity'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-group-seeded-insights" */ '@ms/yammer-web-components/dist/group/PageGroupSeededInsights'
      ),
  },
  {
    path: insightsRootPath,
    loader: () =>
      import(/* webpackChunkName: "4-page-insights" */ '@ms/yammer-insights-page').then((module) => ({
        default: module.PageInsights,
      })),
    dataLoader: () =>
      import(
        /* webpackChunkName: "4-pagedata-insights" */ '@ms/yammer-features-insights/dist/pages/PageInsights/PageDataLoader'
      ),
    childRoutes: [
      {
        path: insightsNetworkSubpath,
        page: 'InsightsNetwork',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-network" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsNetwork'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-insights-network" */ '@ms/yammer-features-insights/dist/pages/PageInsightsNetwork/PageDataLoader'
          ),
        hero: ['network_analytics_activity'],
        exact: true,
      },
      {
        path: insightsSelectedCampaignSubpath,
        page: 'InsightsCampaign',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-campaign" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsCampaign'
          ),
        hero: ['campaign_people_reached_activity'],
        exact: true,
      },
      {
        path: insightsPremiumSelectedGroupSubpath,
        page: 'InsightsGroup',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-group" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsGroup'
          ),
        hero: ['group_analytics_members_activity'],
        exact: true,
      },
      {
        path: insightsQnAOrgSubpath,
        page: 'InsightsQnA',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-qna" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsQnAOrg'
          ),
        hero: ['answers_time_saved_activity'],
        exact: true,
      },
      {
        path: insightsQnAMyInsightsSubpath,
        page: 'InsightsQnAPersonal',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-qna-personal" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsQnAMyInsights'
          ),
        hero: ['answers_people_helped_activity'],
        exact: true,
      },
      {
        path: insightsSelectedAudienceLeaderSubpath,
        page: 'InsightsAudience',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-leaders" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsLeadersOrg'
          ),
        hero: ['audience_analytics_activity'],
        exact: true,
      },
      {
        path: insightsSelectedLeaderMyInsightsSubpath,
        page: 'InsightsPersonalSelectedLeader',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-personal" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsLeadersMyInsights'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-insights-my-insights" */ '@ms/yammer-features-insights/dist/pages/PageInsightsMyInsights/PageDataLoader'
          ),
        hero: ['personal_analytics_activity'],
        exact: true,
      },
      {
        path: insightsMyInsightsSubpath,
        page: 'InsightsPersonal',
        loader: () =>
          import(
            /* webpackChunkName: "4-subpage-insights-personal" */ '@ms/yammer-web-components/dist/insights/SubpageInsightsLeadersMyInsights'
          ),
        dataLoader: () =>
          import(
            /* webpackChunkName: "4-pagedata-insights-my-insights" */ '@ms/yammer-features-insights/dist/pages/PageInsightsMyInsights/PageDataLoader'
          ),
        hero: ['personal_analytics_activity'],
        exact: true,
      },
      {
        path: insightsConversationInsightsSubpath,
        page: 'ConversationAnalytics',
        loader: () => import(/* webpackChunkName: "4-page-conversation-analytics" */ 'pages/PageConversationAnalytics'),
        hero: ['conversation_analytics'],
        exact: true,
      },
    ],
    fallbackRedirectPath: feedPageUrl,
  },
  {
    page: 'AdminSettingsStoryline',
    path: '/admin/settings/storyline',
    exact: true,
    // Storyline: https://yammer.visualstudio.com/engineering/_workitems/edit/344135
    hero: ['admin_storyline_settings'],
    loader: () =>
      import(/* webpackChunkName: "4-page-admin-storyline-settings" */ 'pages/PageStorylineAdminSettingsLifecycle'),
  },
  {
    page: 'AdminSettingsStorylineNotifications',
    path: '/admin/storyline/advanced',
    exact: true,
    // Storyline: https://yammer.visualstudio.com/engineering/_workitems/edit/344135
    hero: ['admin_storyline_settings_advanced'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-admin-storyline-settings-notifications" */ 'pages/PageStorylineAdminAdvancedSettingsLifecycle'
      ),
  },
  {
    page: 'AdminGroupAgentEnabledCommunities',
    path: adminSettingsGroupAgentEnabledCommunitiesUrl,
    exact: true,
    hero: [
      'admin_group_agent_enabled_communities',
      'admin_group_agent_enabled_communities_empty',
      'content_inaccessible',
    ],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-admin-group-agent-enabled-communities" */ 'pages/PageAdminSettingsGroupAgentEnabledCommunities'
      ),
    dataLoader: () =>
      import(
        /* webpackChunkName: "4-pagedata-admin-group-agent-enabled-communities" */ 'pages/PageAdminSettingsGroupAgentEnabledCommunities/AdminFeatureAccessDataLoader'
      ),
  },
  {
    // Auth Team: Migrate to nested routes
    page: 'AdminHome',
    path: '/admin',
    hero: [
      'admin_conversation_settings',
      'admin_external_network_cards',
      'admin_external_network_generate_token',
      'admin_external_network_redeem_code',
      'admin_information_barrier_settings',
      'admin_landing',
      'admin_leader_settings',
      'admin_leader_management_log',
      'admin_leader_management_log_empty',
      'admin_leadership_corner_settings',
      'admin_manage_data_settings',
      'admin_manage_user_achievements',
      'admin_manage_user_roles',
      'admin_manage_user_view_only_mode',
      'admin_mto_policy_configure_hub',
      'admin_mto_policy_view_hub',
      'admin_mute_user',
      'admin_role_management',
      'admin_settings_network_questions',
      'admin_storyline_settings',
      'admin_tenant_configuration',
      'analytics_and_ai_settings',
      'content_inaccessible',
      'designate_leader_tutorial',
    ],
    loader: () => import(/* webpackChunkName: "4-page-admin-settings" */ 'pages/PageAdminSettings'),
  },
  {
    page: 'CuratedThreads',
    path: '/curated-thread-list/:curatedThreadListId/:sourceFileId?/:groupId?',
    exact: true,
    hero: ['curated_thread_list'],
    loader: () => import(/* webpackChunkName: "4-page-curated-threads" */ 'pages/PageCuratedThreadList'),
  },
  {
    page: 'RootRedirect',
    path: '/',
    exact: true,
    hero: ['root_redirect'],
    loader: () => import(/* webpackChunkName: "4-page-root-redirect" */ 'pages/PageRoot'),
  },
  {
    page: 'LegacyRouteConverter',
    path: '*',
    hero: ['legacy_route_converter'],
    loader: () =>
      import(
        /* webpackChunkName: "4-page-legacyrouteconverter" */ '@ms/yammer-web-components/dist/route/LegacyRouteConverter'
      ),
  },
];
